import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { teamQueryKeys } from 'TeamModule/query/teamQueryKeys';
import {
  SearchTeamsByFieldsResponse,
  TeamSearchItem,
  searchTeamsByFieldsApi,
} from 'TeamModule/api/searchTeamsByFieldsApi';

/** Long enough that typing a team name does not fire a search per keystroke. */
const DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;

const teamLabel = (team: TeamSearchItem) => `${team.name} (${team.teamId})`;

/**
 * Pick the team to migrate, scoped to the SOURCE realm.
 *
 * Scoping matters beyond convenience: `runRealmMigration` takes a bare team id and reads
 * it out of whatever realm the headers point at, so a team id that exists in both realms
 * — or a typo that happens to be a real id — migrates the wrong team's data with no
 * warning. Searching the source realm means the id the operator submits is one that was
 * read back from it.
 */
export const SourceTeamSelect = ({
  tenant,
  realmId,
  value,
  onChange,
  scopeHint = 'Choose the source tenant and realm first.',
}: {
  tenant: string;
  realmId: string;
  value: string;
  /** Shown before a tenant and realm are chosen — the card says which ones it means. */
  scopeHint?: string;
  /**
   * Hands up the whole team, not just its id: the search result already carries the
   * team's integrations, so the card can say what would migrate without a second request.
   */
  onChange: (team: TeamSearchItem | null) => void;
}) => {
  /**
   * The chosen team, held rather than looked up in the current page of results.
   *
   * Deriving it from `options` looks equivalent and is not: on select, MUI writes
   * `teamLabel(team)` into the input. If that became the next search, galaxy would
   * substring-match it against the team's own name — which no team can satisfy — the
   * result set would empty, the derived value would flip to null, and MUI's reset would
   * blank the box a moment after the operator picked something.
   */
  const [picked, setPicked] = useState<TeamSearchItem | null>(null);
  const [input, setInput] = useState('');
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  // The parent clears the team when the source scope changes; drop the local copy with it.
  useEffect(() => {
    if (!value) setPicked(null);
  }, [value]);

  // The search term belongs to the realm it was typed against. Keeping it would query the
  // new realm with a filter the operator cannot see — a blank-looking field that
  // nonetheless reports "No teams match that search", or quietly hides the team they want.
  // Reset on the scope itself rather than on `value`, which does not change when a search
  // was typed but nothing was ever selected.
  useEffect(() => {
    setPicked(null);
    setInput('');
    setTyped('');
    setSearch('');
  }, [tenant, realmId]);

  const enabled = !!tenant && !!realmId;

  // tenant + realmId make the api helper send explicit x-tenant / x-realm-id headers rather
  // than inheriting the realm the admin happens to be viewing.
  const params = { tenant, realmId, text: search || undefined, page: 1, limit: PAGE_SIZE };

  const query = useAppQuery<AxiosResponse<SearchTeamsByFieldsResponse>>({
    queryKey: teamQueryKeys.list({ identifier: 'SourceTeamSelect', ...params }),
    queryFn: ({ token, buildConfig }) =>
      searchTeamsByFieldsApi({ params, token, config: buildConfig() }),
    enabled,
    staleTime: 30 * 1000,
  });

  const options: TeamSearchItem[] = useMemo(() => {
    const raw = query.data?.data?.data;
    return Array.isArray(raw) ? raw : [];
  }, [query.data?.data?.data]);

  return (
    <Autocomplete
      options={options}
      value={picked}
      inputValue={input}
      onInputChange={(_, next, reason) => {
        setInput(next);
        // What the server is asked for is NOT simply what is in the box. Galaxy
        // substring-matches `text` against the team name, so a team's own label —
        // "Acme Corp (9383)" — matches nothing, and letting one become the query empties
        // the list under a field that looks filled.
        //
        // This has to key on `reason` rather than on whether `next` is the picked label:
        // MUI fires onInputChange BEFORE onChange, so `picked` is still the previous
        // selection here and cannot answer the question.
        //
        // Three cases, and only typing actually searches:
        // - 'input' — the operator typed; search for it.
        // - 'selectOption' / 'reset' — MUI wrote a label in; searching it finds nothing.
        // - 'blur' / 'clear' — the box was abandoned or emptied. Clear the search rather
        //   than follow the text, because blur RESTORES the selected label (verified
        //   against the component) and searching that has the same empty-list effect.
        //   Clearing also leaves the reopened picker showing the unfiltered first page
        //   instead of filtering by a term that is no longer on screen.
        if (reason === 'input') setTyped(next);
        else if (reason === 'blur' || reason === 'clear') setTyped('');
      }}
      onChange={(_, team) => {
        setPicked(team);
        onChange(team);
      }}
      getOptionLabel={teamLabel}
      isOptionEqualToValue={(a, b) => a.teamId === b.teamId}
      loading={query.isFetching}
      disabled={!enabled}
      // The list is already the server's ranked page for `text`; re-filtering it locally
      // would hide teams the server matched on a field the label does not show.
      filterOptions={(x) => x}
      noOptionsText={search ? 'No teams match that search' : 'Type to search teams'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Team"
          helperText={
            enabled
              ? value
                ? `Team ${value} in ${realmId}`
                : 'One team. The full flow does not support migrating several teams at once.'
              : scopeHint
          }
        />
      )}
    />
  );
};

export default SourceTeamSelect;
